<script setup lang="ts">
  /**
   * PortalFeedAd — dplxapi ads-feed creative rendered as a native feed row.
   *
   * Unlike GenericAPIAd (which renders the classic tile look from ad-ops
   * styleMixins), this renders the ad exactly like the portal FeedList's
   * article rows: image left with an Ad chip, attribution, headline and
   * description in list view; a card in grid view. Tracking mirrors
   * GenericAPIAd: impression on render, viewable at 50%/1s, pixel and
   * click trackers, plus the NtpAd_* analytics events.
   */
  import { onMounted, ref, useTemplateRef } from 'vue'
  import { useIntersectionObserver } from '@vueuse/core'
  import type { DplxApiAd } from '../../ad_ops/providers/dplxapi'
  import { dplxapiProvider } from '../../ad_ops/providers/dplxapi'
  import { useAnalytics } from '../../composables/analytics/useAnalytics'
  import useAdFunctions from '../../composables/ad-ops/useAdFunctions'
  import { useViewabilityTracker } from '../../composables/ad-ops/useViewabilityTracker'
  import { userEngagementStore } from '../../store/userData/UserEngagementStore'

  /**
   * How far below the viewport the creative is prefetched. The impression
   * tracker still fires only on actual viewport entry, so the lookahead
   * removes the skeleton wait without inflating impressions (CTR-safe).
   */
  const PREFETCH_MARGIN_PX = 2000

  const props = withDefaults(
    defineProps<{
      /** Placement key, used for analytics attribution */
      placement: string
      /**
       * How the creative is dressed: a feed row ('list'), a feed card
       * ('grid'), a hero slide ('hero', image filling the frame with
       * the headline overlaid), an info-strip card ('strip', matching
       * InfoStrip's tile chrome — used on mobile where the strip's
       * standard AY unit would collide with the top banner), or a
       * hero sub-row card ('tile', the exact anatomy of the story cards
       * beside it, with the Ad disclosure sitting in the attribution row
       * where the stories show their source).
       */
      view?: 'list' | 'grid' | 'hero' | 'strip' | 'tile'
      /** Fetch only when the row nears the viewport (default) */
      lazyRender?: boolean
    }>(),
    {
      view: 'list',
      lazyRender: true,
    }
  )

  const { track } = useAnalytics()
  const userEngagement = userEngagementStore()
  const { renderWhenCurrentElementIsVisible } = useAdFunctions()

  const ad = ref<DplxApiAd | null>(null)
  const hasTrackedImpression = ref(false)
  const hasEnteredViewport = ref(false)
  const adContainerRef = useTemplateRef<HTMLElement | null>('adContainerRef')

  const viewabilityTracker = useViewabilityTracker(() => trackViewable(), {
    threshold: 0.5,
    viewTime: 1000,
    trackMultiple: false,
  })

  const buildEventData = () =>
    dplxapiProvider.getEventData(
      ad.value
        ? {
            ...ad.value,
            placement: props.placement,
            tilePosition: dplxapiProvider.adIndex || 0,
          }
        : undefined
    )

  async function fetchAd() {
    const fetchedAd = await dplxapiProvider.fetchAd({
      placement: props.placement,
    })
    if (!fetchedAd) return

    ad.value = fetchedAd
    trackImpression()
    if (adContainerRef.value) {
      viewabilityTracker.startTracking(adContainerRef.value)
    }
  }

  // Requires both the creative and viewport entry, in either order:
  // prefetched ads wait for the user, fast scrollers wait for the fetch.
  function trackImpression() {
    if (!ad.value || !hasEnteredViewport.value || hasTrackedImpression.value) {
      return
    }

    dplxapiProvider.trackImpression(ad.value)
    hasTrackedImpression.value = true
    track('NtpAd_Rendered', buildEventData())
  }

  // Once-only firing is owned by useViewabilityTracker (trackMultiple: false);
  // re-checking hasTrackedViewable here would always be true by this point.
  function trackViewable() {
    if (!ad.value) return

    dplxapiProvider.trackViewable(ad.value)
    if (ad.value.pixelTracker) {
      dplxapiProvider.trackPixel(ad.value)
    }
    track('NtpAd_Viewable', buildEventData())
  }

  function handleClick() {
    if (!ad.value) return

    dplxapiProvider.handleAdClick(ad.value)
    track('NtpAd_Clicked', buildEventData())
    userEngagement.engage('ad')
  }

  onMounted(() => {
    // Impression gate: fires when the row itself enters the viewport,
    // matching when a non-prefetched ad would have been requested.
    renderWhenCurrentElementIsVisible(
      adContainerRef.value,
      props.lazyRender,
      false,
      () => {
        hasEnteredViewport.value = true
        trackImpression()
      }
    )

    if (!props.lazyRender) {
      void fetchAd()
      return
    }

    // Prefetch gate: fetch the creative ahead of the scroll so the row
    // is already filled by the time it becomes visible.
    const { stop } = useIntersectionObserver(
      adContainerRef,
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          stop()
          void fetchAd()
        }
      },
      { rootMargin: `${PREFETCH_MARGIN_PX}px 0px`, threshold: 0 }
    )
  })
</script>

<template>
  <div
    ref="adContainerRef"
    :class="
      view === 'hero'
        ? 'absolute inset-0'
        : view === 'tile'
          ? 'h-full w-full'
          : 'w-full'
    "
    data-ad-provider="dplxapi"
  >
    <a
      v-if="ad"
      :href="ad.clickUrl"
      target="_blank"
      rel="noopener noreferrer sponsored"
      :aria-label="`Sponsored: ${ad.title}, ${ad.brand}`"
      class="group cursor-pointer"
      :class="{
        'flex items-start gap-3 py-4 tablet:gap-5': view === 'list',
        block: view === 'grid',
        'absolute inset-0': view === 'hero',
        'flex h-20 w-[312px] items-start justify-between gap-3 rounded-lg bg-surface-primary p-3 transition-colors hover:bg-[var(--yp-card-hover)]':
          view === 'strip',
        'flex h-full flex-col': view === 'tile',
      }"
      @click="handleClick"
    >
      <!-- Hero slide: the creative fills the frame with the headline over
           it, exactly like a story slide, so the ad reads as part of the
           carousel instead of a card wedged inside it -->
      <template v-if="view === 'hero'">
        <img
          :src="ad.imageUrl"
          alt=""
          class="absolute inset-0 h-full w-full object-cover"
        />
        <div
          class="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/90 via-black/40 to-transparent"
        />
        <!-- Disclosure sits in the top left corner on its own, the way
             Yahoo marks a sponsored slide: a compact uppercase AD chip on
             a translucent dark fill rather than a bordered pill next to
             the advertiser -->
        <span
          class="absolute left-3 top-3 rounded-md bg-black/55 px-2 py-0.5 text-[11px] font-bold uppercase leading-4 tracking-wide text-white backdrop-blur"
        >
          Ad
        </span>
        <!-- Advertiser reads as plain text where a story slide puts its
             publisher name, no chip and no border. It shares one bottom
             anchored column with the headline, so a
             long headline pushes the brand line up rather than growing into
             it, matching the story slides. -->
        <div
          class="absolute bottom-5 left-5 right-16 flex flex-col gap-2 tablet:right-28"
        >
          <p
            class="text-[15px] font-semibold text-white drop-shadow-[0_1px_3px_rgba(0,0,0,0.9)]"
          >
            {{ ad.brand }}
          </p>
          <h2
            class="line-clamp-3 text-2xl font-bold leading-[1.15] text-white group-hover:underline tablet:text-[32px]"
          >
            {{ ad.title }}
          </h2>
        </div>
      </template>

      <!-- Strip card: same chrome as InfoStrip's tiles — kicker row with
           the compact Ad badge and the brand, title, thumbnail centered
           on the right -->
      <template v-else-if="view === 'strip'">
        <div class="min-w-0">
          <p class="flex items-center gap-1.5">
            <span
              class="shrink-0 rounded bg-[var(--yp-active)] px-1.5 py-0.5 text-[9px] font-bold uppercase text-content-primary"
            >
              Ad
            </span>
            <span class="truncate text-xs text-content-secondary">
              {{ ad.brand }}
            </span>
          </p>
          <p
            class="mt-1 line-clamp-2 text-[13px] font-semibold leading-snug text-content-primary"
          >
            {{ ad.title }}
          </p>
        </div>
        <img
          :src="ad.imageUrl"
          alt=""
          class="h-14 w-14 shrink-0 self-center rounded-lg object-cover"
          loading="lazy"
        />
      </template>

      <!-- Sub-row tile: the exact anatomy of the story cards beside it —
           16/9 image, the same 15px three-line title, and the disclosure
           in the attribution row where the stories name their source -->
      <template v-else-if="view === 'tile'">
        <img
          :src="ad.imageUrl"
          alt=""
          class="aspect-[16/9] w-full rounded-md object-cover"
          loading="lazy"
        />
        <p
          class="mt-3 line-clamp-3 text-[15px] font-semibold leading-snug text-content-primary group-hover:underline"
        >
          {{ ad.title }}
        </p>
        <p
          class="mt-auto flex items-center gap-2 pt-2.5 text-[13px] text-content-secondary"
        >
          <span
            class="rounded border border-[var(--yp-chip-border)] px-1.5 py-0.5 text-[10px] font-bold uppercase leading-none text-content-primary"
          >
            Ad
          </span>
          <span class="truncate">{{ ad.brand }}</span>
        </p>
      </template>

      <!-- Feed row and card views -->
      <template v-else>
        <!-- Card view: Ad chip header sits above the media, like the
             publisher header on article cards -->
        <p
          v-if="view === 'grid'"
          class="mb-3 flex items-center gap-1.5 text-xs text-content-secondary"
        >
          <span
            class="rounded-full border border-[var(--yp-chip-border)] px-2.5 py-0.5 text-[12px] font-bold text-content-primary"
          >
            Ad
          </span>
          <span class="text-[13px]">{{ ad.brand }}</span>
        </p>

        <div
          class="relative aspect-[16/9] overflow-hidden rounded-lg"
          :class="view === 'list' ? 'w-[42%] max-w-[340px] shrink-0' : ''"
        >
          <img
            :src="ad.imageUrl"
            alt=""
            class="h-full w-full object-cover"
            loading="lazy"
          />
        </div>

        <div class="min-w-0" :class="view === 'grid' ? 'mt-3' : ''">
          <p
            v-if="view === 'list'"
            class="flex items-center gap-1.5 text-xs text-content-secondary"
          >
            <span
              class="rounded-full border border-[var(--yp-chip-border)] px-2.5 py-0.5 text-[12px] font-bold text-content-primary"
            >
              Ad
            </span>
            <span class="text-[13px]">{{ ad.brand }}</span>
          </p>

          <!-- Matches FeedList exactly: title only, 22px/28px semibold at
               tablet+, underline on hover in list view -->
          <h3
            class="line-clamp-3 font-semibold text-content-primary"
            :class="
              view === 'list'
                ? 'mt-1 text-base leading-snug group-hover:underline tablet:text-[22px] tablet:leading-7'
                : 'text-xl leading-snug tablet:text-2xl'
            "
          >
            {{ ad.title }}
          </h3>
        </div>
      </template>
    </a>

    <!-- Skeleton while the creative loads. Hero slides pulse via their own
         frame, so they only need the frame filled. -->
    <div
      v-else-if="view === 'hero'"
      class="absolute inset-0 bg-[var(--yp-hover)]"
    />
    <div
      v-else-if="view === 'strip'"
      class="h-20 w-[312px] animate-pulse rounded-lg bg-[var(--yp-hover)]"
    />
    <div
      v-else
      :class="view === 'list' ? 'flex items-start gap-3 py-4 tablet:gap-5' : ''"
    >
      <div
        class="aspect-[16/9] rounded-lg bg-[var(--yp-hover)]"
        :class="view === 'list' ? 'w-[42%] max-w-[340px] shrink-0' : 'w-full'"
      />
      <div
        class="min-w-0 flex-1 space-y-2.5"
        :class="view === 'grid' ? 'mt-3' : ''"
      >
        <div class="h-3 w-16 rounded bg-[var(--yp-hover)]" />
        <div class="h-5 w-3/4 rounded bg-[var(--yp-hover)]" />
        <div class="h-5 w-1/2 rounded bg-[var(--yp-hover)]" />
      </div>
    </div>
  </div>
</template>
